<script setup lang="ts">
import type { DosageForm } from '@/api/medications';
import { useMobile } from '@/composables/useMobile';
import { routeLabel } from '@/utils/medications';
import MedicationDosageFields from './medication-dosage-fields.vue';

// Second step: one card per dosage, how much and when. Several dosages are
// different doses at different times of the same order ("1 en la mañana, 2
// en la noche"). The drug and its route are the first step's, read in the
// stepper; here only where it is not shown (on phones, or on a change, which
// has no drug step). A high-alert drug is flagged next to the dose.
defineProps<{
  drugName: string;
  route: string;
  doseUnit: string;
  doseUnitLabel: string;
  highAlert: boolean;
  isChange: boolean;
  start: Date | null;
  showErrors: boolean;
}>();
const emit = defineEmits<{ addDosage: [] }>();
const dosages = defineModel<DosageForm[]>('dosages', { required: true });
const { isPhone } = useMobile();
</script>
<template>
  <div class="d-flex flex-column ga-4">
    <div
      v-if="isPhone || isChange"
      class="d-flex flex-wrap align-center ga-1"
    >
      <span class="text-subtitle-1 font-weight-medium mr-1">{{ drugName }}</span>
      <span>Vía {{ routeLabel(route).toLowerCase() }}</span>
    </div>

    <v-alert
      v-if="highAlert"
      type="error"
      variant="tonal"
      density="compact"
      icon="mdi-alert"
      text="Alto riesgo: revisa dos veces la dosis y los horarios."
    />

    <medication-dosage-fields
      v-for="(dosage, position) in dosages"
      :key="position"
      v-model="dosages[position]"
      :title="dosages.length > 1 ? `Dosis ${position + 1}` : ''"
      :dose-unit="doseUnit"
      :dose-unit-label="doseUnitLabel"
      :start="start"
      :show-errors="showErrors"
      @remove="dosages.splice(position, 1)"
    />
    <div>
      <v-btn
        variant="outlined"
        size="small"
        prepend-icon="mdi-plus"
        @click="emit('addDosage')"
      >
        Agregar otra dosis
      </v-btn>
      <div class="text-caption text-medium-emphasis mt-1">
        Para una cantidad distinta en otro horario, como 1 en la mañana y 2 en la noche.
      </div>
    </div>
  </div>
</template>
